import { useEffect, useState } from 'react';
import {
  LOADING_TIP_DELAY_MS,
  LOADING_TIP_MIN_VISIBLE_MS,
  hintHoldRemaining,
} from '@/constants/loadingTips';

interface UseHeldLoadingOptions {
  /** 表示までの遅延(ms)。これ未満で loading が終わると一度も表示しない（チラつき防止）。 */
  delayMs?: number;
  /** 一度表示したら最低これだけ表示を維持する(ms)。 */
  minVisibleMs?: number;
}

/**
 * 全画面ローディング（LoadingTips 等）の表示可否を「最低表示時間つき」で管理するフック（#205）。
 *
 * `React.lazy`（Suspense）側は `lazyWithMinDisplay` で同等の制御をしているが、`isLoading` で
 * 制御する各ページのデータ取得ローダーは、loading が false になった瞬間にアンマウントされ最低
 * 表示時間を持てない。本フックが返す真偽で描画を出し分けることで、両者の挙動を揃える。
 *
 * 挙動:
 * - loading が `delayMs` 未満で終わる → 一度も true にしない（速いロードは出さない）。
 * - 一度 true にしたら、loading が先に false になっても `minVisibleMs` 経過まで true を維持。
 * - loading 継続中はずっと true。
 *
 * 使い方:
 *   const showLoader = useHeldLoading(isLoading);
 *   if (showLoader) return <LoadingTips delayMs={0} />; // 遅延は本フックが担うので 0
 */
export function useHeldLoading(
  loading: boolean,
  {
    delayMs = LOADING_TIP_DELAY_MS,
    minVisibleMs = LOADING_TIP_MIN_VISIBLE_MS,
  }: UseHeldLoadingOptions = {},
): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout> | undefined;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;

    if (loading) {
      if (!visible) {
        // delayMs 経過後に表示（速いロードは出さない）
        showTimer = setTimeout(() => setVisible(true), delayMs);
      }
    } else if (visible) {
      // 表示中に loading 終了 → **共有の**最低表示時間（ヒント表示開始からの残り）まで維持。
      // オーバーレイと遷移先データローダーが別々に数えて合計が伸びるのを防ぐため、per-instance では
      // なく hintHoldRemaining（全ローダー共有の起点）を使う。remaining<=0 でも setTimeout 経由で
      // 消し、effect 本体での同期 setState を避ける（react-hooks/set-state-in-effect）。
      const remaining = hintHoldRemaining(minVisibleMs);
      hideTimer = setTimeout(() => setVisible(false), remaining);
    }

    return () => {
      if (showTimer) clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [loading, visible, delayMs, minVisibleMs]);

  return visible;
}
